

<template>
  <section>
    <!-- Page content-->
    <div class="content-wrapper">
      <div class="content-heading">
        Home
      </div>
      <div class="row">
        <spinner v-if="loading"></spinner>
        <div class="col-md-12">
          <div class="panel panel-default">
            <!-- Welcome Message -->
            <div class="welcome text-center">
              <header class="welcome-header">
                  <h3 class="welcome-title">Welcome to your financial advisor portal <br>at Calvert Impact Capital</h3>
              </header>
              <p class="welcome-paragraph">
                 We invite you to learn <a target="_blank" rel="noopener" aria-describedby="new-window-0" href="https://www.calvertimpactcapital.org/about">about our work <em class="fa fa fa-external-link"></em></a>.
                 <br>Or proceed to manage your clients' accounts.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="row tutorial-row">
        <div class="col-md-12">
          <div class="panel panel-default">
            <div class="tutorial">
              <header class="tutorial-header">
                  <h3 class="tutorial-title">How to access your client's accounts</h3>
                  <div class="line-separator"></div>
              </header>
              <div>
                <div class="tutorial-step">
                  <div class="image-holder">
                    <img src="../assets/img/tutorial_1.png" alt="step-1">
                  </div>
                  <ol>
                    <li>
                      <strong>Click on your name at the top right of the page.</strong>
                      <p>A dropdown with links to your account and your clients' accounts will be shown.</p>
                      </li>
                  </ol>
                </div>
                <div class="tutorial-step">
                  <div class="image-holder">
                    <img src="../assets/img/tutorial_2.png" alt="step-2">
                  </div>
                  <ol start="2" id="step-2">
                    <li>
                      <strong>Once you see the dropdown, click on one of your clients' accounts.</strong>
                      <p>Switch between client accounts from the dropdown.</p>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex';

  import Spinner from '@/components/widgets/Spinner';

  export default {
    name: 'home',
    data() {
      return {
        loading: false,
        isWelcomeMessageDisplayed: false,
      };
    },
    computed: {
      ...mapState('app', [
        'activeAccount',
        'user',
        'userTypes',
      ]),
      ...mapGetters('app', [
        'hasBeenCreatedAccount',
      ]),
    },
    async beforeMount() {
      this.loading = true;
      await this.determineWhatToDoWithLoggedInUser();
      this.loading = false;
    },
    methods: {
      ...mapActions('home', [
        'getUserStatus',
      ]),
      async determineWhatToDoWithLoggedInUser() {
        try {
          const accountId = this.activeAccount.id;

          if (this.shouldWelcomeMessageBeDisplayed()) {
            return true;
          }

          // Without a real account there is no status to fetch. Asking for
          // account 0 returns a 403, which the HttpService interceptor turns
          // into a redirect to Home.
          if (!accountId) {
            return false;
          }

          const response = await this.getUserStatus({
            accountId,
          });

          const { status, needs } = response;

          if (this.shouldUserBeRedirectedBecauseOfStatus(status)) {
            return true;
          } else if (this.shouldUserBeRedirectedBecauseOfInformationNeeds(needs)) {
            return true;
          }

          return false;
        } catch (error) { // eslint-disable-line
          return false;
        }
      },
      shouldUserBeRedirectedBecauseOfInformationNeeds(needs) {
        let needsAsString;

        if (!needs.length) {
          needsAsString = 'false';
        } else if (needs.includes('FUNDING_SOURCE')) {
          needsAsString = 'FUNDING_SOURCE';
        } else if (needs.includes('NOTE')) {
          needsAsString = 'NOTE';
        }

        const options = {
          FUNDING_SOURCE: () => this.$router.push({ name: 'AddFundingSource' }),
          NOTE: () => this.$router.push({ name: 'PurchaseNote', query: { first: 1 } }),
          false: () => this.$router.push({ name: 'AccountSummary' }),
        };

        if (typeof options[needsAsString] === 'function') {
          options[needsAsString]();
        }

        return typeof options[needsAsString] === 'function';
      },
      shouldUserBeRedirectedBecauseOfStatus(status) {
        if (!this.hasBeenCreatedAccount) {
          this.$router.push({ name: 'AccountSummary' });
          return true;
        }

        const options = {
          suspended: () => this.$router.push({ name: 'AccountSuspended' }),
          retry: () => this.$router.push({ name: 'AccountCreation' }),
          document: () => this.$router.push({ name: 'DocumentUpload' }),
        };

        if (typeof options[status] === 'function') {
          options[status]();
        }

        return typeof options[status] === 'function';
      },
      shouldWelcomeMessageBeDisplayed() {
        const { userType } = this.user;

        this.isWelcomeMessageDisplayed =
          (userType === this.userTypes.FINANCIAL_ADVISOR.name);

        return this.isWelcomeMessageDisplayed;
      },
    },
    components: {
      spinner: Spinner,
    },
  };
</script>
<style lang="scss" scoped>

  /* Welcome Styles */
  .welcome {
    background-color: white;
    color: #515253;
    border: none;
    padding-bottom: 40px;
    margin: 0 auto;
    p {
        font-size: 18px;
    }
  }
  .welcome-header {
    margin: 40px auto 0;
  }
  .welcome-title {
    font-size: 22px;
  }
  .welcome-paragraph {
    max-width: 70%;
    margin: 20px auto;
    em {
      opacity: 0.65;
      font-size: 12px;
    }
    a {
      color: #00788d;
      &:hover em {
          opacity: 1;
      }
    }
  }

  aside.aside + section {
    margin-bottom: 0!important;
  }
  .panel.panel-default {
    margin-bottom: 0;
    padding: 0;
  }

  .tutorial-body {
    margin: 0 15%;
  }

  .tutorial-title {
    color: #007a8f;
    font-size: 20px;
  }

  .line-separator {
      padding: 8px;
      line-height: 1.52857143;
      vertical-align: top;
      border-top: 1px solid #eee;
    }

  .tutorial-step {
    display: flex;
    margin: 25px 0;
    font-size: 18px;
    ol {
      font-weight: bold;
      li {
        color: #007a8f;
        strong {
          color: #000;
          margin-left: 10px;
        }
        p {
          color: #515253;
          font-weight: 300;
          margin-left: 10px;
        }
      }
    }
  }

  .image-holder {
    text-align: center;
    margin-right: 25px;
  }
  .tutorial {
    padding: 5px 25px;
  }

  #step-2 {
    margin-top: 180px;
  }

  .tutorial-row {
    margin-top: 25px;
  }
</style>



// WEBPACK FOOTER //
// Home.vue?2868d4dc